import {
  WebGLRenderer, Scene, PerspectiveCamera, Color, Vector2,
  WebGLRenderTarget, LinearSRGBColorSpace, HalfFloatType, RGBAFormat,
  ACESFilmicToneMapping,
} from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';

// Render-target height + DPR caps are tiered by device class. The ray-march
// fragment shader runs ~96 steps per pixel, so per-frame pixel count is the
// dominant cost. A single budget that's safe on a phone is blurry on an
// iMac; a single budget that's sharp on iMac chokes an MBA.
//
// Three layers feed into the per-frame budget:
//
//   1. Quality preset (user override, persisted to localStorage). When set
//      to anything other than 'auto', it pins RT height + DPR caps to fixed
//      values regardless of the detected device tier. Defaults to 'auto'.
//   2. Device tier (used when preset is 'auto'). Phone / laptop / desktop
//      heuristic based on viewport width + DPR.
//   3. AdaptiveScaler runtime scale (0..1). Multiplies into the RT after
//      caps are picked. Still active even with a manual preset so we still
//      protect against thermal events or background-tab demotion.
const PHONE_VIEWPORT_PX = 768;
const LAPTOP_MAX_VIEWPORT_PX = 1600;
const LAPTOP_MIN_DPR = 1.5;

const TIER_PRESETS = {
  phone:   { rt: 540, dpr: 1.5 },
  laptop:  { rt: 720, dpr: 1.5 },
  desktop: { rt: 900, dpr: 2.0 },
};

export const QUALITY_PRESETS = {
  auto:    null, // sentinel: defer to device tier
  low:     { rt: 480,  dpr: 1.0 },
  medium:  { rt: 720,  dpr: 1.5 },
  high:    { rt: 900,  dpr: 2.0 },
  ultra:   { rt: 1080, dpr: 2.0 },
};

export const QUALITY_PRESET_NAMES = ['auto', 'low', 'medium', 'high', 'ultra'];

const LS_QUALITY_KEY = 'dp-quality-preset';

function readStoredPreset() {
  try {
    const stored = (typeof localStorage !== 'undefined') ? localStorage.getItem(LS_QUALITY_KEY) : null;
    return QUALITY_PRESET_NAMES.includes(stored) ? stored : 'auto';
  } catch (_) {
    return 'auto';
  }
}

// Module-level so rtMaxHeight() / maxDpr() can read it without plumbing the
// renderer instance through. Initialized from localStorage on import so the
// first resize() picks up the user's last choice.
let _qualityPreset = readStoredPreset();

function deviceTier() {
  const w = window.innerWidth;
  const dpr = window.devicePixelRatio || 1;
  if (w < PHONE_VIEWPORT_PX) return 'phone';
  if (w <= LAPTOP_MAX_VIEWPORT_PX && dpr >= LAPTOP_MIN_DPR) return 'laptop';
  return 'desktop';
}

function activePreset() {
  const override = QUALITY_PRESETS[_qualityPreset];
  if (override) return override;
  return TIER_PRESETS[deviceTier()];
}

function rtMaxHeight() {
  return activePreset().rt;
}

function maxDpr() {
  return activePreset().dpr;
}

// Caller passes a name from QUALITY_PRESET_NAMES. Unknown / null / invalid
// input falls back to 'auto'. After the override changes, callers should
// trigger a renderer.resize() so the new caps take effect.
export function setQualityPreset(name) {
  const next = QUALITY_PRESET_NAMES.includes(name) ? name : 'auto';
  _qualityPreset = next;
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(LS_QUALITY_KEY, next);
  } catch (_) { /* private mode / storage full */ }
  return next;
}

export function getQualityPreset() {
  return _qualityPreset;
}

// Post-FX chain (M7):
//   RenderPass (scene → HDR RT, MSAA 4x, HalfFloat)
//   → UnrealBloomPass (half-res — 4x cheaper than full, visually indistinguishable for a blur)
//   → OutputPass (ACES tonemap + sRGB conversion to backbuffer)
//
// Bloom strength/threshold are driven per-frame from audio in main.js via
// setBloom({ strength, threshold }). Radius is left static — changing it
// regenerates internal mips, which is expensive.
export class Renderer {
  constructor(canvas) {
    this.canvas = canvas;
    this.three = new WebGLRenderer({ canvas, antialias: false, alpha: false });
    this.three.outputColorSpace = LinearSRGBColorSpace;
    this.three.setClearColor(new Color(0x000000), 1);
    this.three.toneMapping = ACESFilmicToneMapping;
    this.three.toneMappingExposure = 1.0;

    this.scene = new Scene();
    this.camera = new PerspectiveCamera(60, 1, 0.1, 200);
    this.camera.position.set(0, 2, 8);
    this.camera.lookAt(0, 1.5, -5);

    this.composer = null;
    this.renderPass = null;
    this.bloomPass = null;
    this.outputPass = null;
    this.resScale = 1;
    this.resize();
    window.addEventListener('resize', () => this.resize());
  }

  setScale(scale) {
    if (scale === this.resScale) return;
    this.resScale = scale;
    this.resize();
  }

  resize() {
    const dpr = Math.min(window.devicePixelRatio, maxDpr());
    const cw = this.canvas.clientWidth || window.innerWidth;
    const ch = this.canvas.clientHeight || window.innerHeight;
    this.three.setPixelRatio(dpr);
    this.three.setSize(cw, ch, false);
    this.camera.aspect = cw / ch;
    this.camera.updateProjectionMatrix();

    const aspect = cw / ch;
    const rtH = Math.min(ch * dpr, rtMaxHeight()) * this.resScale;
    const rtW = rtH * aspect;
    const w = Math.max(1, Math.round(rtW));
    const h = Math.max(1, Math.round(rtH));
    this._setupComposer(w, h);
  }

  _makeHdrRT(w, h) {
    const hasFloat = !!this.three.getContext().getExtension('EXT_color_buffer_float');
    return new WebGLRenderTarget(w, h, {
      type: hasFloat ? HalfFloatType : undefined,
      format: RGBAFormat,
      samples: 4,
    });
  }

  _setupComposer(w, h) {
    if (!this.composer) {
      const rt = this._makeHdrRT(w, h);
      this.composer = new EffectComposer(this.three, rt);
      this.renderPass = new RenderPass(this.scene, this.camera);
      // Half-res bloom resolution.
      this.bloomPass = new UnrealBloomPass(
        new Vector2(Math.max(1, Math.round(w * 0.5)), Math.max(1, Math.round(h * 0.5))),
        0.8, // strength
        0.6, // radius
        0.6, // threshold
      );
      this.outputPass = new OutputPass();
      this.composer.addPass(this.renderPass);
      this.composer.addPass(this.bloomPass);
      this.composer.addPass(this.outputPass);
    } else {
      this.composer.setSize(w, h);
      this.bloomPass.setSize(Math.max(1, Math.round(w * 0.5)), Math.max(1, Math.round(h * 0.5)));
    }
  }

  setBloom({ strength, radius, threshold } = {}) {
    if (!this.bloomPass) return;
    if (strength !== undefined) this.bloomPass.strength = strength;
    if (radius !== undefined) this.bloomPass.radius = radius;
    if (threshold !== undefined) this.bloomPass.threshold = threshold;
  }

  render() {
    this.composer.render();
  }

  dispose() {
    if (this.composer) {
      this.composer.renderTarget1.dispose();
      this.composer.renderTarget2.dispose();
    }
    this.three.dispose();
  }
}
